// #765 Story D View 1 — per-conversation hand. Renders inside
// AdminConversationDetail.js as a self-contained section that
// fetches its own data and hides itself when the call has no
// card_outcomes rows (i.e. predates the cards system).
//
// One row per dealt card: card_key + stratum + position + outcome
// (one of the five #886 flat states, colour-coded) + engagement_score
// (if played) + duration_on_card_seconds.
import React, { useEffect, useState } from 'react';
import { adminFetch } from './userSubPageStyles';
import { ENGAGEMENT_LABEL } from './labels';

// v1.5.0 — #886: flat outcome taxonomy. The pre-#886 (outcome,
// skip_reason) two-field shape collapsed into a single `outcome`
// string drawn from FIVE flat states (played / declined / ignored
// / passed_over / unraised). `passed_over` subsumes the pre-#886
// offered_among_many + sibling_won sub-states. `unraised`
// subsumes forgot + conversation_rich. The "Skip reason" column
// is removed; per-state semantics live in the OUTCOME_GLOSSARY
// tooltip on the Outcome column header (and per-cell `title`).
// v1.4.1 — #881: extend the Skip-reason glossary with the
// `sibling_won` sub-state. Multi-card topic clusters (e.g.
// `talk_hobby:gardening` + `media_personal_music` +
// `game_interactive:domino` all dealt on one call, user
// names one) now classify the un-named siblings as
// `skipped:sibling_won` instead of the misleading `declined` /
// `offered_among_many`. Tooltip line follows the same shape as
// the other four sub-states.
// v1.4.0 — #876: admin-clarity pass on the Dealer-hand panel —
// outcome legend above the hand table, Skip-reason values
// self-explained via column-header tooltip + per-cell title
// attribute, section heading renamed to `Conversation segments`
// (covers both `card_played:` and `wildcard:` segments — the
// old `Wildcard / off-script` label only described half of
// what's there), per-segment column header `Tag` renamed to
// `Topic raised` to break the "played" overload at the
// column-header level (the underlying segmenter tag prefix
// `card_played:` stays — renaming would touch persisted rows
// and is out of scope for #876).
// v1.3.0 — #839: Hand-and-Outcomes panel — per-card rationale
// + clickable evidence-turn chips. Stratum 2 cards are now
// filtered server-side and don't render here. Empty-state
// message shown when no Stratum 1 cards were dealt.
// v1.2.0 — #806: render the wildcard section (segment count +
// top topics + per-segment table) replacing the pre-#806
// "wildcard tracking not yet enabled" placeholder.
// v1.1.0 — #767 Story F: render `display_label` for open_thread
// cards (human-readable topic_summary) instead of the raw
// `talk_open_thread:<uuid>` card_key.
// v1.7.0 — THREE concurrent changes landed in the admin-bundle PR:
//   #922: split dealer-hand and cross-credit into two sections.
//     Dealer hand table no longer carries the synthetic "position -1"
//     cross-credit row (it confused admins because the
//     hand_position=-1 sentinel sorted those rows to the top via
//     ORDER BY hand_position ASC). The new "Cross-credited from this
//     call" sub-section below the main hand surfaces the synthetic
//     rows with their source-segment audit trail (segment id + idx +
//     quality + rationale). API contract change: response now carries
//     `dealt` rows in `hand` and `synthetic` rows in `cross_credit`.
//   #924: turn numbering switched from user-turn 1-based (post-#910)
//     to interleaved chronological 1-based. Turn 1 = AI greeting,
//     Turn 2 = elderly's first reply, Turn 3 = AI's reply, etc. The
//     Turns grid in CallDiagnostics now renders one row per transcript
//     entry (assistant AND user, interleaved) and the DOM-id contract
//     becomes `turn-${i+1}` where i is the 0-based combined
//     transcript-jsonb index. Evidence chips and segment ranges in
//     this panel display the same combined-1-based numbers — chips no
//     longer dedupe across assistant/user pairs because each
//     combined-index is now its own turn. Storage stays as
//     transcript-jsonb indices; conversion is presentational only.
//   #927: unify admin vocabulary. Column headers and tooltip copy now
//     use the canonical "Engagement" term sourced from `./labels.js`
//     (the `ENGAGEMENT_LABEL` constant). The pre-#927 tooltip phrasing
//     "Engagement score for this card..." normalised to
//     "Engagement for this card..." — the underlying backend field
//     `card_outcomes.engagement_score` stays put.
// v1.6.1 — Engagement-column tooltips on both tables (Dealer hand
// composite + Conversation segments LLM judgment) so admins don't
// have to remember which one means what and how they differ.
// v1.6.0 — #910: render evidence chips and segment turn ranges as
// user-turn numbers (1-based, deduped) instead of raw transcript-jsonb
// indices. Click-to-scroll now lands on the same physical row the
// Turns grid labels with the same number.
// v1.8.0 — #1055: resurface the "Conversation segments" view (played
// cards + played wildcards). The per-segment breakdown table now
// renders EXPANDED by default (was hidden behind a "Show per-segment
// breakdown" <details> toggle since #806), and the section renders an
// explicit empty-state when a call has a dealt hand but zero segments
// instead of vanishing. The #896 tabification + the collapse together
// made this view read as "lost". (Why a rich call can have zero
// segments — the segmenter silently returns [] on long calls — is
// tracked separately in #1056.) Admin-only, English-only.
const VERSION = 'v1.8.0';

// #839 — copy shown when a card was generated before the
// rationale field was persisted (dealer_notes.rationale null).
const RATIONALE_NOT_AVAILABLE = 'Rationale not available (pre-feature)';
// Copy shown when the call has zero Stratum 1 card outcomes —
// either it predates the cards system, the dealer dealt only
// Stratum 2, or recording failed. Replaces the prior silent
// hide-the-panel behaviour per AC #839.
const EMPTY_HAND_MESSAGE = 'No dealt hand recorded for this call.';
// #1055 — copy shown in the "Conversation segments" section when a
// call HAS a dealt hand but the post-call segmenter produced zero
// segments. Pre-#1055 the section was hidden entirely on zero
// segments, so a blank space read as "the view is gone" — most
// visibly on long calls, where the segmenter can silently return []
// (#1056). Rendering an explicit empty-state keeps the section present
// and tells the admin WHY it's empty.
const SEGMENTS_EMPTY_MESSAGE =
  'No conversation segments were recorded for this call. The post-call '
  + 'segmenter can return none — e.g. on very long calls (#1056), or for '
  + 'calls that predate the segmenter (#806).';

// #839 — scroll the matching Turns-grid row into view. CallDiagnostics
// (separate component) tags each row with `id="turn-${i+1}"` where i
// is the 0-based combined transcript-jsonb index (#924). We
// deliberately use a direct DOM lookup over lifting the data up into
// AdminConversationDetail — the components are otherwise independent
// and a prop wire-up just for this would add coupling.
function scrollToTurnLabel(label) {
  if (typeof window === 'undefined' || label == null) return;
  const el = document.getElementById(`turn-${label}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Briefly flash the row so the admin can see WHICH turn we scrolled
  // to — without this the scroll lands them somewhere in a busy table
  // and they have to count rows to confirm.
  el.classList.add('turn-flash');
  setTimeout(() => el.classList.remove('turn-flash'), 1600);
}

// #924 — canonical turn-number convention: interleaved 1-based
// chronological. Turn 1 = AI greeting (transcript-jsonb idx 0),
// Turn 2 = elderly's first reply (idx 1), Turn 3 = AI's reply
// (idx 2), Turn 4 = elderly's reply (idx 3), etc.
//
// Pre-#910 the dealer hand showed `evidence_turn_indices` as raw
// transcript-jsonb 0-based combined indices; the Turns grid showed
// `turn_diagnostics.turn_index` (user-turn-1-based) — different
// numbers for the same physical row. #910 picked user-turn-1-based
// for all surfaces. Bas's retest experience (issue #924) showed that
// the natural reading of a transcript expects turns numbered in the
// order they're spoken, regardless of speaker — "Turn 7" should be
// the 7th utterance chronologically, not the 7th user reply.
//
// #924 conversion: transcript-jsonb idx `i` → label = `i + 1` (1-based
// combined). Storage convention stays as 0-based transcript-jsonb
// indices; conversion is purely presentational. Backend
// `turn_diagnostics.turn_index` stays user-turn-1-based (avoids a
// data migration); the Turns grid maps user reply N (turn_index=N)
// to combined idx 2N-1, i.e. label 2N (see CallDiagnostics.js).
//
// Function renamed from #910's `transcriptIdxToUserTurn` to make
// the new convention legible at the call site. Single canonical
// helper — a future drift can't reintroduce dual conventions.
export function transcriptIdxToTurnLabel(i) {
  if (i == null || !Number.isFinite(i) || i < 0) return null;
  return i + 1;
}

// Build the ordered list of turn labels a transcript-jsonb index
// array refers to. Renamed from `evidenceIndicesToUserTurns` and no
// longer dedupes — each combined-index is its own turn under the
// #924 convention, so the array maps element-by-element.
export function evidenceIndicesToTurnLabels(indices) {
  if (!Array.isArray(indices)) return [];
  const out = [];
  for (const i of indices) {
    const t = transcriptIdxToTurnLabel(i);
    if (t == null) continue;
    out.push(t);
  }
  return out;
}

// Compact a sequence of turn labels into contiguous ranges, e.g.
// `[14, 15, 16, 18]` → `["14-16", "18"]`. Used to render the dealer-
// hand evidence chips as ranges instead of one chip per turn when
// the evidence spans contiguous combined indices (typical case: a
// 9-turn segment becomes one "Turn 14-22" chip, not nine chips).
export function compactTurnLabelsToRanges(labels) {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  // Sort + dedupe to make the contiguity check straightforward; the
  // input order isn't load-bearing for the dealer-hand summary view.
  const sorted = [...new Set(labels.filter(n => Number.isFinite(n)))].sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  const ranges = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let k = 1; k < sorted.length; k++) {
    const n = sorted[k];
    if (n === prev + 1) {
      prev = n;
      continue;
    }
    ranges.push(start === prev ? String(start) : `${start}-${prev}`);
    start = n;
    prev = n;
  }
  ranges.push(start === prev ? String(start) : `${start}-${prev}`);
  return ranges;
}

// Label a combined-1-based turn number for display. Trivial under the
// #924 convention (no greeting special-case — Turn 1 IS the greeting),
// kept as a named export so call sites that hold a label number
// remain self-documenting and a future label-format change has one
// place to edit.
export function turnLabelString(t) {
  if (t == null) return '';
  return String(t);
}

// #886 — colour mapping for the five flat outcome states. `passed_over`
// and `unraised` are visually neutral (no strong signal in either
// direction — the card was dealt but the user didn't refuse). `ignored`
// is amber (light negative — pivot without engagement). `declined` is
// red (explicit refusal). `played` is green.
const outcomeColors = {
  played:      { bg: '#E8F5E9', fg: '#2E7D32' },  // green
  declined:    { bg: '#FFEBEE', fg: '#C62828' },  // red
  ignored:     { bg: '#FFF8E1', fg: '#795548' },  // amber
  passed_over: { bg: '#F3E5F5', fg: '#6A1B9A' },  // soft purple — neutral, not negative
  unraised:    { bg: '#ECEFF1', fg: '#546E7A' },  // soft grey — non-signal
};

// #886 — outcome glossary keyed on the flat 5-state taxonomy. Replaces
// the pre-#886 OUTCOME_LEGEND (which only covered the legacy 3 states)
// AND SKIP_REASON_GLOSSARY (which covered the 5 sub-states under
// "skipped"). The pre-#886 (skipped + skip_reason='ignored') becomes
// the flat `ignored` state; (skipped + offered_among_many | sibling_won)
// becomes `passed_over`; (skipped + forgot | conversation_rich) becomes
// `unraised`. The two-state collapse for `passed_over` and `unraised`
// reflects that the sub-state distinctions weren't actionable for the
// weight system (both halves of each pair triggered identical
// no-decay behaviour) and weren't useful for the admin in practice.
const OUTCOME_GLOSSARY = {
  played:
    'User engaged with the card subject.',
  declined:
    'User actively refused (explicit "no").',
  ignored:
    'Berta raised it; the user pivoted to a different topic without acknowledging or refusing.',
  passed_over:
    'User picked a related card from the same topic cluster, OR selected one option from a multi-option offer turn; this card was not refused.',
  unraised:
    'Berta never raised it during the call.',
};

const OUTCOME_LEGEND = [
  { key: 'played',      text: OUTCOME_GLOSSARY.played },
  { key: 'declined',    text: OUTCOME_GLOSSARY.declined },
  { key: 'ignored',     text: OUTCOME_GLOSSARY.ignored },
  { key: 'passed_over', text: OUTCOME_GLOSSARY.passed_over },
  { key: 'unraised',    text: OUTCOME_GLOSSARY.unraised },
];

function outcomeTooltip(outcome) {
  if (!outcome) return '';
  return OUTCOME_GLOSSARY[outcome] || '';
}

function fmtDuration(secs) {
  if (secs == null) return '—';
  const n = Math.round(Number(secs));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const s = n % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function outcomeBadge(outcome) {
  const c = outcomeColors[outcome] || { bg: '#EEE', fg: '#666' };
  return {
    display: 'inline-block',
    padding: '2px 8px', borderRadius: 10,
    fontSize: '0.72rem', fontWeight: 600,
    background: c.bg, color: c.fg,
    textTransform: 'capitalize',
  };
}

function engagementBadge(score) {
  if (score == null) return null;
  const w = Math.max(0, Math.min(100, Number(score)));
  // Colour mirrors the topic-weight bucket colours from
  // AdminUserMemory.js for cross-page consistency.
  let bg, fg;
  if (w >= 70)      { bg = '#E8F5E9'; fg = '#2E7D32'; }
  else if (w >= 30) { bg = '#FFF3E0'; fg = '#E65100'; }
  else              { bg = '#FFEBEE'; fg = '#C62828'; }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: '0.78rem', fontWeight: 700,
      background: bg, color: fg,
    }}>{Math.round(w)}</span>
  );
}

export default function CardsHandView({ convId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminFetch(
          `/api/admin/conversations/${convId}/hand`
        );
        if (cancelled) return;
        setData(r || {});
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, [convId]);

  // #839 — render even on empty hand, with an explanatory message.
  // Pre-#839 we hid the entire panel silently for any call with no
  // card_outcomes rows; the new copy makes the empty case readable
  // (admin sees "this call has no dealt hand" instead of a missing
  // section they have to guess about). Errors still fall through.
  if (!data && !error) return null;

  return (
    <div className="transcript-container" style={{ marginTop: 24 }}>
      <div className="transcript-title">
        Dealer hand <span style={{ color: '#aaa', fontWeight: 400 }}>{VERSION}</span>
      </div>
      {error && <p className="error-state">Error: {error}</p>}

      {data && (!data.hand || data.hand.length === 0) && !error && (
        <p style={{
          color: '#888', fontSize: '0.88rem', fontStyle: 'italic',
          margin: '6px 0 0',
        }}>
          {EMPTY_HAND_MESSAGE}
        </p>
      )}

      {data.hand && data.hand.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          {/* #876 — outcome legend rendered above the table so the
              colour-coded badges in the Outcome column are
              self-explanatory without bouncing to the engagement
              classifier source. */}
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', gap: 12,
              alignItems: 'center',
              margin: '6px 0 10px', fontSize: '0.78rem', color: '#666',
            }}
          >
            {OUTCOME_LEGEND.map(item => (
              <span key={item.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={outcomeBadge(item.key)}>{item.key}</span>
                <span>— {item.text}</span>
              </span>
            ))}
          </div>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem',
          }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>#</th>
                <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Card</th>
                {/* #886 — Outcome column carries the full state
                    semantics now that the flat taxonomy collapsed
                    skip_reason in. Tooltip on the column header gives
                    the per-state definition without a separate column. */}
                <th
                  style={{ padding: '8px 6px', color: '#666', fontWeight: 600, cursor: 'help' }}
                  title={
                    'Outcome states (hover a row badge for the per-card definition):\n'
                    + `• played — ${OUTCOME_GLOSSARY.played}\n`
                    + `• declined — ${OUTCOME_GLOSSARY.declined}\n`
                    + `• ignored — ${OUTCOME_GLOSSARY.ignored}\n`
                    + `• passed_over — ${OUTCOME_GLOSSARY.passed_over}\n`
                    + `• unraised — ${OUTCOME_GLOSSARY.unraised}`
                  }
                >Outcome</th>
                <th
                  style={{ padding: '8px 6px', color: '#666', fontWeight: 600, cursor: 'help' }}
                  title={
                    'Engagement for this card across the WHOLE call (0–100, deterministic composite).\n'
                    + '\n'
                    + 'Computed by compute_engagement_score() from 8 weighted components:\n'
                    + '  • sustained_turns (weight 0.20) — turns the card subject stayed on the table\n'
                    + '  • volunteered_detail (0.15) — user turns with rich detail about the card\n'
                    + '  • mood_delta (0.15) — call-entry → call-exit mood shift\n'
                    + '  • verbosity (0.15) — tokens-per-turn on-card vs call baseline\n'
                    + '  • duration_on_card (0.10) — real activity wall-clock OR turn proxy\n'
                    + '  • latency (0.10) — reply latency (currently neutral placeholder)\n'
                    + '  • prosody (0.10) — voice warmth (currently neutral placeholder)\n'
                    + '  • no_disengagement (0.05) — no silence-ladder / early hangup\n'
                    + '  + listen_credit bonus (#892) — additive when card triggered a song play\n'
                    + '\n'
                    + 'Effect on card_weights.weight (the persistent learning surface):\n'
                    + '  ≥ 70 — full positive bump\n'
                    + '  [50, 70) — half bump (#893)\n'
                    + '  [30, 50) — deliberate no-movement band\n'
                    + '  < 30 — decay\n'
                    + '\n'
                    + 'Distinct from the Conversation segments engagement_quality below '
                    + '(that one is a per-segment LLM impression; this one is a deterministic '
                    + 'composite across the whole call and DRIVES the gauge in /memory).'
                  }
                >{ENGAGEMENT_LABEL}</th>
                <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Duration</th>
                <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600, minWidth: 220 }}>Rationale</th>
                <th style={{ padding: '8px 6px', color: '#666', fontWeight: 600 }}>Evidence turns</th>
              </tr>
            </thead>
            <tbody>
              {data.hand.map((c, i) => (
                <tr key={`${c.card_key}-${i}`} style={{ borderBottom: '1px solid #f3f3f3', verticalAlign: 'top' }}>
                  <td style={{ padding: '8px 6px', color: '#aaa' }}>{c.position ?? i + 1}</td>
                  <td style={{ padding: '8px 6px', fontFamily: 'ui-monospace, monospace' }}>
                    {/* #767 — open_thread cards have UUID
                        suffixes; the backend pre-joins the
                        topic_summary into `display_label` so the
                        admin can tell at a glance which recall
                        was on this hand. Other card types have
                        readable slug suffixes already (e.g.
                        `talk_pet:fleeli`) so they fall through
                        to the raw card_key. */}
                    {c.display_label || c.card_key}
                  </td>
                  {/* #886 — outcome badge carries a per-cell tooltip
                      with the flat-state definition. `cursor: help`
                      hints that the badge has more info. */}
                  <td style={{ padding: '8px 6px' }}>
                    <span
                      style={{ ...outcomeBadge(c.outcome), cursor: 'help' }}
                      title={outcomeTooltip(c.outcome)}
                    >
                      {c.outcome}
                    </span>
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    {c.engagement_score != null
                      ? engagementBadge(c.engagement_score)
                      : <span style={{ color: '#ccc' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 6px', color: '#666' }}>
                    {fmtDuration(c.duration_on_card_seconds)}
                  </td>
                  {/* #839 — Rationale column. Null for calls dealt
                      before the rationale field shipped; we surface
                      that explicitly so the admin doesn't have to
                      wonder if generation failed silently. */}
                  <td style={{ padding: '8px 6px', color: '#2C2C2C', fontSize: '0.84rem' }}>
                    {c.rationale
                      ? c.rationale
                      : <span style={{ color: '#aaa', fontStyle: 'italic' }}>{RATIONALE_NOT_AVAILABLE}</span>}
                  </td>
                  {/* #839 — Evidence-turn chips. Pulled from
                      `wildcard_segments` rows tagged
                      `card_played:<key>` for this call. Clicking
                      a chip scrolls + flashes the matching row in
                      the Turns grid below (sibling component;
                      DOM-id contract `turn-${idx}`). Empty when
                      the call predates #806 or the segmenter
                      didn't match this card. */}
                  <td style={{ padding: '8px 6px' }}>
                    {/* #924 — render interleaved-1-based turn labels
                        (Turn 1 = AI greeting, Turn 2 = elderly reply 1,
                        Turn 3 = AI reply, ...). Contiguous runs collapse
                        into range chips ("#14-22") so a 9-turn segment
                        renders as one chip, not nine. Each chip click
                        scrolls to the FIRST label in its range; the
                        Turns grid row has DOM id `turn-${label}`. */}
                    {Array.isArray(c.evidence_turn_indices) && c.evidence_turn_indices.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {compactTurnLabelsToRanges(
                          evidenceIndicesToTurnLabels(c.evidence_turn_indices)
                        ).map(rangeLabel => {
                          // Extract the first turn number in the range
                          // for the scroll target ("14-22" → 14).
                          const first = parseInt(String(rangeLabel).split('-')[0], 10);
                          return (
                            <button
                              key={rangeLabel}
                              type="button"
                              onClick={() => scrollToTurnLabel(first)}
                              title={`Jump to Turn ${rangeLabel} in the Turns grid`}
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px', borderRadius: 10,
                                fontSize: '0.72rem', fontWeight: 600,
                                background: '#E8F5E9', color: '#2E7D32',
                                border: '1px solid #C8E6C9',
                                cursor: 'pointer',
                                fontFamily: 'ui-monospace, monospace',
                              }}
                            >
                              #{rangeLabel}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ color: '#ccc' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* #922 — Cross-credited sub-section. Renders synthetic
          card_outcomes rows (#912) whose hand_position = -1 sentinel
          flags "this card was NOT dealt but received engagement
          signal from the user discussing it inside a card_played
          segment for a different card." Audit-trail fields (source
          segment id + idx + quality) make the synthetic rationale
          traceable back to the originating segment. Hidden when the
          call has no cross-credit rows (the common case). */}
      {Array.isArray(data.cross_credit) && data.cross_credit.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4
            style={{ marginBottom: 6, cursor: 'help' }}
            title={
              'These cards were NOT dealt but received engagement '
              + 'signal via the user discussing them inside a '
              + 'card_played segment for a different card.\n\n'
              + '#912 cross-credit: e.g. user warmly discussed '
              + '"Dire Straits" inside a card_played:media_personal_music '
              + 'segment, but talk_singer:dire_straits was not in the '
              + 'dealt hand. A synthetic card_outcomes row is written '
              + 'with engagement_score = round(quality × '
              + 'CROSS_CREDIT_SEGMENT_WEIGHT), and (if the score is '
              + 'high enough) the artist\'s card_weights gauge moves.'
            }
          >
            Cross-credited from this call ({data.cross_credit.length})
          </h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem',
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: '6px 4px', color: '#666', fontWeight: 600 }}>Card</th>
                  <th style={{ padding: '6px 4px', color: '#666', fontWeight: 600 }}>Score</th>
                  <th style={{ padding: '6px 4px', color: '#666', fontWeight: 600 }}>From segment</th>
                  <th style={{ padding: '6px 4px', color: '#666', fontWeight: 600 }}>Source quality</th>
                  <th style={{ padding: '6px 4px', color: '#666', fontWeight: 600, minWidth: 240 }}>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {data.cross_credit.map((c, i) => (
                  <tr key={`xc-${c.card_key}-${i}`} style={{ borderBottom: '1px solid #f3f3f3', verticalAlign: 'top' }}>
                    <td style={{ padding: '6px 4px', fontFamily: 'ui-monospace, monospace' }}>
                      {c.display_label || c.card_key}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      {c.engagement_score != null
                        ? engagementBadge(c.engagement_score)
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td
                      style={{ padding: '6px 4px', color: '#666', fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}
                      title={
                        c.cross_credited_from
                          ? `segment id: ${c.cross_credited_from}`
                          : 'source segment id missing'
                      }
                    >
                      {c.cross_credit_source_segment_idx != null
                        ? `#${c.cross_credit_source_segment_idx}`
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      {c.cross_credit_source_engagement_quality != null
                        ? engagementBadge(c.cross_credit_source_engagement_quality)
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '6px 4px', color: '#2C2C2C', fontSize: '0.84rem' }}>
                      {c.rationale
                        ? c.rationale
                        : <span style={{ color: '#aaa', fontStyle: 'italic' }}>{RATIONALE_NOT_AVAILABLE}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* #806 / #1055 — "Conversation segments" (played cards +
          played wildcards): per-call summary, top topics, and the
          per-segment table. #1055 RESURFACE: the per-segment table
          renders EXPANDED by default (was a "Show per-segment
          breakdown" <details> toggle), and a sibling empty-state
          block below covers the case where the call has a dealt hand
          but the segmenter produced zero segments (#1056) — so the
          view no longer silently vanishes. This block stays gated on
          segment_count > 0 (the populated path). */}
      {data.wildcard && data.wildcard.segment_count > 0 && (
        <div style={{ marginTop: 20 }}>
          {/* #876 — heading renamed from `Wildcard / off-script` to
              `Conversation segments`: the section renders BOTH
              `card_played:` (on-script) AND `wildcard:` (off-script)
              rows; the old label only described half. The inline
              summary paragraph below still spells out the split
              ("X on a dealt card, Y wildcard"). */}
          <h4 style={{ marginBottom: 6 }}>Conversation segments</h4>
          <p style={{ color: '#666', fontSize: '0.82rem', marginTop: 0 }}>
            {data.wildcard.segment_count} segment{data.wildcard.segment_count === 1 ? '' : 's'}
            {' — '}
            <strong>{data.wildcard.card_played_count}</strong> on a dealt card,
            {' '}
            <strong>{data.wildcard.wildcard_count}</strong> wildcard
            {data.wildcard.avg_wildcard_engagement != null && (
              <> · avg wildcard engagement <strong>{data.wildcard.avg_wildcard_engagement}</strong></>
            )}
          </p>
          {data.wildcard.top_topics && data.wildcard.top_topics.length > 0 && (
            <p style={{ fontSize: '0.82rem', color: '#666' }}>
              <span style={{ color: '#888' }}>Top wildcard topics: </span>
              {data.wildcard.top_topics.slice(0, 5).map((t, i) => (
                <span key={t.topic}>
                  {i > 0 && ', '}
                  <em>{t.topic}</em>
                  {' '}<span style={{ color: '#aaa' }}>
                    ({t.count}{t.avg_engagement != null ? `, ${t.avg_engagement}` : ''})
                  </span>
                </span>
              ))}
            </p>
          )}
          {data.wildcard.segments && data.wildcard.segments.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {/* #1055 — expanded by default (was a <details>/<summary>
                  collapse). The played-cards + played-wildcards
                  breakdown is the whole point of this view, so it
                  shouldn't take an extra click to see. */}
              <div style={{ color: '#888', fontSize: '0.82rem', marginBottom: 4 }}>
                Per-segment breakdown ({data.wildcard.segments.length}) — admin only
              </div>
              <div style={{ overflowX: 'auto', marginTop: 6 }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  fontSize: '0.84rem', marginTop: 6,
                }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: '6px 4px', color: '#666' }}>Turns</th>
                      {/* #876 — column renamed from `Tag` →
                          `Topic raised` to break the "played"
                          overload at the column-header level:
                          the underlying tag prefix `card_played:`
                          here means "Berta uttered the card's
                          subject in this turn range" — distinct
                          from the Outcome column's `played` which
                          means "user engaged with the subject".
                          The raw `card_played:<key>` / `wildcard:`
                          tag string still renders in the cell. */}
                      <th style={{ padding: '6px 4px', color: '#666' }}>Topic raised</th>
                      <th style={{ padding: '6px 4px', color: '#666' }}>Topic</th>
                      <th
                        style={{ padding: '6px 4px', color: '#666', cursor: 'help' }}
                        title={
                          'Per-segment engagement_quality (0–100, LLM judgment).\n'
                          + '\n'
                          + 'Haiku is asked, segment by segment:\n'
                          + '  "Rate engagement_quality 0–100: how warmly did the user\n'
                          + '   engage? 0 = brushed off / monosyllabic; 50 = polite\n'
                          + '   participation; 90+ = enthusiastic, volunteered detail."\n'
                          + '\n'
                          + 'It looks ONLY at this segment\'s turns — not the whole call.\n'
                          + 'No components, no weighted formula — a single integer per\n'
                          + 'segment, the LLM\'s gut read of those turns.\n'
                          + '\n'
                          + 'Distinct from the Dealer hand Engagement column above\n'
                          + '(that one is a deterministic composite across the whole\n'
                          + 'call for one card_key and DRIVES the gauge in /memory).\n'
                          + 'This segment-side score is OBSERVATIONAL only — used for\n'
                          + 'taxonomy-gap detection and admin readability, not the\n'
                          + 'learning surface. The two can (and often do) disagree:\n'
                          + 'one LLM-warm 5-turn stretch may show 60 here even if\n'
                          + 'the whole-call composite for that card lands lower.'
                        }
                      >{ENGAGEMENT_LABEL}</th>
                      <th style={{ padding: '6px 4px', color: '#666' }}>Excerpt (admin-only)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.wildcard.segments.map(s => {
                      // #924 — render the segment turn range as
                      // interleaved-1-based labels (Turn 1 = greeting),
                      // matching the dealer-hand evidence chips above
                      // and the Turns grid below. Storage convention
                      // stays as 0-based transcript-jsonb indices;
                      // conversion is purely presentational.
                      const labelStart = transcriptIdxToTurnLabel(s.turn_start);
                      const labelEnd = transcriptIdxToTurnLabel(s.turn_end);
                      const sameTurn = labelStart === labelEnd;
                      const rangeLabel = sameTurn
                        ? `#${turnLabelString(labelStart)}`
                        : `#${turnLabelString(labelStart)}–${turnLabelString(labelEnd)}`;
                      return (
                      <tr key={s.segment_idx} style={{ borderBottom: '1px solid #f3f3f3' }}>
                        <td style={{ padding: '6px 4px', color: '#aaa' }}
                            title={`storage indices: ${s.turn_start}–${s.turn_end}`}
                        >
                          {rangeLabel}
                        </td>
                        <td style={{ padding: '6px 4px', fontFamily: 'ui-monospace, monospace', color: s.tag?.startsWith('wildcard:') ? '#6A1B9A' : '#2E7D32' }}>
                          {s.tag}
                        </td>
                        <td style={{ padding: '6px 4px', color: '#444' }}>
                          {s.inferred_topic || '—'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          {s.engagement_quality != null
                            ? engagementBadge(s.engagement_quality)
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 4px', color: '#888', fontSize: '0.78rem', fontStyle: 'italic' }}>
                          {s.text_excerpt
                            ? `"${s.text_excerpt}"`
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* #1055 — empty-state sibling for "Conversation segments".
          Renders when the call HAS a dealt hand but the segmenter
          produced zero segments (segment_count === 0). Pre-#1055 the
          whole section was gated on segment_count > 0 and silently
          disappeared here, which read as "the view is gone" — most
          visibly on long calls where the segmenter returns [] (#1056).
          Shows the API `note` (pre-#806 backward-compat path) when
          present, else the generic empty-state copy. Not shown for
          calls with no hand at all — the empty-hand message above
          covers those. */}
      {data.wildcard && data.wildcard.segment_count === 0
        && data.hand && data.hand.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h4 style={{ marginBottom: 6 }}>Conversation segments</h4>
          <p style={{
            color: '#888', fontSize: '0.85rem', fontStyle: 'italic',
            marginTop: 0,
          }}>
            {data.wildcard.note || SEGMENTS_EMPTY_MESSAGE}
          </p>
        </div>
      )}
    </div>
  );
}
